import {queries} from '@/shared/integrations/zero'
import {Pharmacy} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {useCallback, useMemo, useState} from 'react'
import {ListPharmaciesInput} from '@cypro/engine/queries'

export type PharmacyStartRow = Pick<Pharmacy, 'id' | 'createdAt' | 'name'>

export function useGetInfinitePharmacies({
  sort = {dir: 'asc', field: 'name'},
  filters,
  limit = 25,
}: Omit<Partial<ListPharmaciesInput>, 'cursor'> = {}) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters: {status: ['active'] as const, ...filters}, limit, search}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [sort?.dir, sort?.field, filters?.name, filters?.status, limit, search]
  )

  const getPageQuery: GetPageQuery<Pharmacy, PharmacyStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.createdAt
          ? {id: start.id, direction: dir, createdAt: start.createdAt, name: start.name}
          : undefined

      return {
        query: queries.pharmacies.listPharmacies({
          cursor,
          limit: pageLimit,
          sort,
          filters: {status: ['active'] as const, ...filters, ...(search ? {name: search} : {})},
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getSingleQuery: GetSingleQuery<Pharmacy> = useCallback(
    ({id}) => ({
      query: queries.pharmacies.getPharmacyById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (row: Pharmacy): PharmacyStartRow => ({
      id: row.id,
      createdAt: row.createdAt,
      name: row.name,
    }),
    []
  )

  const transformToSelectItem = useCallback((row: Pharmacy) => ({label: row.name, value: row.id}), [])

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
    transformToSelectItem,
  }
}
